import { cva, type VariantProps } from 'class-variance-authority';
import { disabledState, focusRing, transitionBase, uiMarker } from './shared';

/**
 * BUTTON
 *
 * `variant` is about intent (how loud is this action?), `size` is about the
 * control height it snaps to. Heights come from --control-height-* so a button
 * and an input placed side by side always line up.
 *
 * The `icon` size is a square button for an icon on its own — it still needs an
 * aria-label, which the Button component enforces in development.
 */
export const buttonRecipe = cva(
  [
    uiMarker,
    'inline-flex items-center justify-center gap-2 whitespace-nowrap align-middle',
    'rounded-md font-sans text-body font-medium',
    'select-none',
    // Any icon passed as a child is sized here so callers never have to.
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
    transitionBase,
    focusRing,
    disabledState,
    // The href form renders an <a>, which never matches :disabled — it says
    // aria-disabled instead, and has to look and behave the same.
    'aria-disabled:pointer-events-none aria-disabled:opacity-50',
  ],
  {
    variants: {
      /*
       * Each variant carries a `data-[force=hover]` twin of its hover rule, so
       * the catalogue can show the hover state without a pointer. Written out
       * rather than derived because Tailwind reads class names from source.
       */
      variant: {
        /** The one main action on a screen. Use at most one per view. */
        primary:
          'bg-primary text-primary-foreground shadow-e1 hover:bg-primary/90 data-[force=hover]:bg-primary/90',
        /** A supporting action that still deserves a filled button. */
        secondary:
          'bg-secondary text-secondary-foreground shadow-e1 hover:bg-secondary/80 data-[force=hover]:bg-secondary/80',
        /** Equal-weight action next to a primary one. */
        outline:
          'border border-input bg-background text-foreground shadow-e1 hover:bg-accent hover:text-accent-foreground data-[force=hover]:bg-accent data-[force=hover]:text-accent-foreground',
        /** Lowest-emphasis action: toolbars, table row actions, icon buttons. */
        ghost:
          'text-foreground hover:bg-accent hover:text-accent-foreground data-[force=hover]:bg-accent data-[force=hover]:text-accent-foreground',
        /** Deletes, cancellations, anything the user cannot undo. */
        destructive:
          'bg-destructive text-destructive-foreground shadow-e1 hover:bg-destructive/90 data-[force=hover]:bg-destructive/90',
        /** Confirms a positive outcome — marking a call complete, for example. */
        success:
          'bg-success text-success-foreground shadow-e1 hover:bg-success/90 data-[force=hover]:bg-success/90',
        /**
         * A light button on a filled primary panel: the last "Get started"
         * on the sales page's closing band, where primary would vanish.
         */
        inverse:
          'bg-card text-card-foreground shadow-e1 hover:bg-card/90 data-[force=hover]:bg-card/90',
        /**
         * The second action on a filled primary panel, next to an inverse
         * button: "Book a call" beside "Get started" on the sales page.
         */
        'inverse-outline':
          'border border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10 data-[force=hover]:bg-primary-foreground/10',
        /** A softly filled supporting action on a primary panel. */
        'inverse-soft':
          'bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20 data-[force=hover]:bg-primary-foreground/20',
        /** Looks like a link, behaves like a button. */
        link: 'text-primary underline-offset-4 hover:underline data-[force=hover]:underline',
        /**
         * A ghost that starts muted and comes up to full colour on hover —
         * the close, dismiss and "more" buttons at the edge of a panel.
         * Thirty-two sites wrote this as ghost plus two colour classes.
         */
        subtle:
          'text-muted-foreground hover:bg-muted hover:text-foreground data-[force=hover]:bg-muted data-[force=hover]:text-foreground',
        /** A ghost for a destructive row action: red text, no fill until hover. */
        'destructive-ghost':
          'text-destructive hover:bg-destructive-subtle hover:text-destructive data-[force=hover]:bg-destructive-subtle',
        /**
         * An outline for a destructive action among a row of outline pills —
         * Delete beside Edit on a detail card. Red text and a faint red rule
         * rather than a filled red button, which would read as the thing the
         * card is for.
         */
        'destructive-outline':
          'border border-destructive/30 bg-background text-destructive shadow-e1 hover:bg-destructive-subtle hover:text-destructive data-[force=hover]:bg-destructive-subtle',
        /**
         * An outline button in its chosen state, for a question answered by
         * pressing one of two: Pass or Fail, Yes or No. The unchosen one is
         * `outline`; the chosen one keeps the outline's shape and takes the
         * subtle fill of its meaning, so the pair reads as one control.
         */
        'success-selected':
          'border border-success bg-success-subtle text-success-subtle-foreground shadow-e1 hover:bg-success-subtle data-[force=hover]:bg-success-subtle',
        'destructive-selected':
          'border border-destructive bg-destructive-subtle text-destructive-subtle-foreground shadow-e1 hover:bg-destructive-subtle data-[force=hover]:bg-destructive-subtle',
        'primary-selected':
          'border border-primary bg-accent text-accent-foreground shadow-e1 hover:bg-accent data-[force=hover]:bg-accent',
        /**
         * "Add another": a dashed outline in muted text that takes the
         * primary tint on hover. The studio's add-service buttons and the
         * booking editor drew this three times as a ghost with eight classes.
         */
        dashed:
          'border border-dashed border-border text-muted-foreground hover:border-primary/40 hover:bg-primary/5 hover:text-primary data-[force=hover]:border-primary/40 data-[force=hover]:bg-primary/5 data-[force=hover]:text-primary',
        /**
         * A control laid over a picture — the remove button on an uploaded
         * photo. A dark scrim with a white glyph in either theme, for the
         * reason a lightbox is dark in both: it sits on the image, not the
         * page, and a scrim that flipped with the theme would vanish on a
         * light photo in dark mode.
         */
        /**
         * No chrome in any state. For a clickable region whose container
         * already carries the affordance — the title block of a card that
         * expands, the day label beside a switch — where a ghost's hover
         * fill would paint a box around one part of a row.
         */
        plain: 'text-foreground',
        overlay:
          // design-tokens-ignore -- sits on a photo, not on a themed surface; see above.
          'bg-black/60 text-white hover:bg-black/75 hover:text-white data-[force=hover]:bg-black/75',
        /**
         * A control floating over the page's own content — the previous and
         * next arrows at the edges of a photo strip. Page-coloured and a
         * little translucent so what is behind shows through, solid on hover.
         * `overlay` is the other case: a control on the picture itself, which
         * wants a dark scrim. Six sites wrote this as outline plus five
         * classes, all of them pills.
         */
        floating:
          'border border-input bg-background/80 text-foreground shadow-e2 backdrop-blur-sm hover:bg-background data-[force=hover]:bg-background',
        /**
         * An editable value, drawn as the box a read-only one sits in.
         *
         * The impound detail tab shows each field as a filled box that
         * becomes an input when clicked. It matches readOnlyValueRecipe —
         * same fill, minimum height, inset and weight — so an editable value
         * and a fixed one read as the same kind of thing, and only trying
         * tells them apart. The height comes from the content so a joined
         * list can wrap.
         */
        value:
          'min-h-control-md justify-start whitespace-normal bg-muted/50 text-left font-normal text-foreground hover:bg-muted data-[force=hover]:bg-muted',
      },
      /** Fully rounded ends — chips, the studio's floating controls. */
      pill: {
        true: 'rounded-pill',
        false: '',
      },
      size: {
        /**
         * 28 high, between the scale's xs (24) and sm (32): the action pills
         * along the top of a detail card, which sit beside a row of 25-high
         * chips rather than in a form, so lining up with an Input is not
         * what they have to do. Pair with `pill`.
         */
        xs: 'h-7 px-2.5 text-caption [&_svg]:size-icon-sm',
        /**
         * Packed: 30 high, a 4px inset and a 4px gap. Grouping and Filters in
         * a list's toolbar, where at the scale's 12px padding these two plus
         * the view switch and the magnifier overflow a 400 column.
         */
        compact: 'h-[30px] gap-1 px-1 text-body-sm [&_svg]:size-icon-sm',
        sm: 'h-control-sm px-3 text-body-sm [&_svg]:size-icon-sm',
        md: 'h-control-md px-4 [&_svg]:size-icon-sm',
        lg: 'h-control-lg px-6 [&_svg]:size-icon-md',
        /** A full-page form's primary action. Signup and onboarding use it. */
        xl: 'h-control-xl px-8 text-body-lg [&_svg]:size-icon-md',
        /**
         * Content-shaped: no height, no padding.
         *
         * For a trigger with no chrome — an icon in a table cell, a word that
         * acts as a link. 64 of these were written as `h-auto p-0`, which is
         * this variant spelled by hand, and which the height check could not
         * tell apart from a control that had simply opted out of the scale.
         */
        inline: 'h-auto p-0 [&_svg]:size-icon-sm',
        /** Square, icon-only. */
        icon: 'h-control-md w-control-md p-0 [&_svg]:size-icon-sm',
        'icon-xs': 'h-control-xs w-control-xs p-0 [&_svg]:size-icon-xs',
        'icon-sm': 'h-control-sm w-control-sm p-0 [&_svg]:size-icon-sm',
        'icon-lg': 'h-control-lg w-control-lg p-0 [&_svg]:size-icon-md',
      },
      /** Stretch to fill the container. Useful in mobile sheets and forms. */
      /**
       * Let the height come from the content.
       *
       * `size` keeps meaning padding and type — this only drops the fixed
       * height, for a button whose label wraps or that stacks two lines. 63 of
       * these were written as `h-auto` plus hand-picked padding, which tangled
       * two decisions into one className and made a growing button
       * indistinguishable from one that had simply opted out of the scale.
       */
      grow: {
        true: 'h-auto',
        false: '',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
      /**
       * Hidden until the row or frame it sits in is hovered — the remove
       * button on an uploaded photo, the hide control on a rail row. The
       * parent carries `group`. Same meaning as DragHandle's `reveal`; six
       * sites wrote it out as three opacity classes.
       *
       * A revealed trigger stays shown while what it opened is open: the
       * pointer leaves the row for the popover, and the button that opened
       * it must not vanish underneath. Radix stamps `data-state="open"` on
       * the trigger, which is the signal.
       */
      reveal: {
        true: 'opacity-0 transition-all group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100',
        false: '',
      },
    },
    compoundVariants: [
      // A link-styled button should not carry button padding or height.
      { variant: 'link', size: 'sm', class: 'h-auto px-0' },
      { variant: 'link', size: 'md', class: 'h-auto px-0' },
      { variant: 'link', size: 'lg', class: 'h-auto px-0' },
      // A value box grows with its content and sits on the read-only inset.
      { variant: 'value', size: 'sm', class: 'h-auto px-3 py-2' },
      { variant: 'value', size: 'md', class: 'h-auto px-3 py-2' },
      { variant: 'value', size: 'lg', class: 'h-auto px-3 py-2' },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      grow: false,
      fullWidth: false,
      pill: false,
      reveal: false,
    },
  }
);

export type ButtonRecipeProps = VariantProps<typeof buttonRecipe>;
